import {runInAction} from 'mobx';

import {delay} from '@yourcoach/async-utils';

import type {Nullable} from '../../../utils/utility-types';

import {type Destructible, FlagManager} from '../../../utils/store';
import {type VideoPlayerState} from './VideoPlayerState';

export class VideoPlayerStateDomListenersManager implements Destructible {
  isEndedFlag = new FlagManager(false);
  isFocusedFlag = new FlagManager(false);
  isMetadataLoaded = new FlagManager(false);
  isPausedFlag = new FlagManager(true);
  shouldShowControlsFlag = new FlagManager(true);
  private animationFrameId: Nullable<number> = null;
  private DELAY_MS = 100;
  private STEP_SECONDS = 5;

  constructor(private state: VideoPlayerState) {
    this.bindListeners();
  }

  destruct() {
    const player = this.state.player;

    if (player) {
      player.removeEventListener('loadstart', this.onLoadStartHandler);
      player.removeEventListener('loadedmetadata', this.onLoadMetadataHandler);
      player.removeEventListener('timeupdate', this.onTimeUpdateHandler);
      player.removeEventListener('pause', this.onPauseHandler);
      player.removeEventListener('play', this.onPlayHandler);
      player.removeEventListener('error', this.onErrorHandler);
      player.removeEventListener('focus', this.onFocusHandler);
    }

    const container = this.state.container;

    if (container) {
      container.removeEventListener('mouseenter', this.onMouseEnter);
      container.removeEventListener('mouseleave', this.onMouseLeave);
    }

    document.removeEventListener('keydown', this.onKeyboardShortcutsHandler);

    this.stopCurrentTimeTracking();
  }

  initContainer() {
    const container = this.state.container;

    if (container) {
      container.addEventListener('mouseenter', this.onMouseEnter);
      container.addEventListener('mouseleave', this.onMouseLeave);
    }
  }

  initPlayer() {
    const player = this.state.player;

    if (player) {
      player.addEventListener('loadstart', this.onLoadStartHandler);

      player.addEventListener('loadedmetadata', this.onLoadMetadataHandler);

      player.addEventListener('timeupdate', this.onTimeUpdateHandler);
      player.addEventListener('pause', this.onPauseHandler);
      player.addEventListener('play', this.onPlayHandler);
      player.addEventListener('error', this.onErrorHandler);
      player.addEventListener('focus', this.onFocusHandler);

      document.addEventListener('keydown', this.onKeyboardShortcutsHandler);
    }
  }

  onErrorHandler(event: ErrorEvent) {
    this.state.onError(event);
  }

  private bindListeners() {
    this.onErrorHandler = this.onErrorHandler.bind(this);
    this.onFocusHandler = this.onFocusHandler.bind(this);
    this.onKeyboardShortcutsHandler =
      this.onKeyboardShortcutsHandler.bind(this);
    this.onLoadMetadataHandler = this.onLoadMetadataHandler.bind(this);
    this.onLoadStartHandler = this.onLoadStartHandler.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onPauseHandler = this.onPauseHandler.bind(this);
    this.onPlayHandler = this.onPlayHandler.bind(this);
    this.onTimeUpdateHandler = this.onTimeUpdateHandler.bind(this);
  }

  private hideControls() {
    if (this.state.isPaused || this.shouldShowControlsFlag.isFalse) {
      return;
    }

    this.shouldShowControlsFlag.setFalse();
  }

  private onFocusHandler() {
    this.isFocusedFlag.setTrue();
  }

  private async onKeyboardShortcutsHandler(event: KeyboardEvent) {
    const {code} = event;

    // TODO: need to tune how we detect focused state (maybe we should use intersection observer as well)
    // handle only when video is focused
    if (this.isFocusedFlag.isFalse) {
      return;
    }

    switch (code) {
      case 'ArrowLeft':
        this.state.seek(this.state.currentTimeSec - this.STEP_SECONDS);
        break;
      case 'ArrowRight':
        this.state.seek(this.state.currentTimeSec + this.STEP_SECONDS);
        break;
      case 'KeyF':
        this.state.toggleFullscreen();
        break;
      case 'KeyK':
      case 'Space': {
        if (this.state.isPaused || this.state.isEnded) {
          this.state.play();

          if (this.isFocusedFlag.isFalse) {
            await delay(this.DELAY_MS);
            this.hideControls();
          }
        } else {
          this.state.pause();

          if (this.isFocusedFlag.isFalse) {
            await delay(this.DELAY_MS);
            this.showControls();
          }
        }
        break;
      }
      case 'KeyM':
        this.state.toggleMute();
        break;
    }
  }

  private onLoadMetadataHandler() {
    this.isMetadataLoaded.setTrue();
  }

  private onLoadStartHandler() {
    if (this.isMetadataLoaded.isTrue) {
      this.isMetadataLoaded.setFalse();
    }
  }

  private onMouseEnter() {
    this.showControls();

    this.isFocusedFlag.setTrue();
  }

  private onMouseLeave() {
    this.hideControls();

    this.isFocusedFlag.setFalse();
  }

  private onPauseHandler() {
    this.isPausedFlag.setTrue();

    this.stopCurrentTimeTracking();
  }

  private onPlayHandler() {
    this.isPausedFlag.setFalse();

    this.startCurrentTimeTracking();
  }

  private onTimeUpdateHandler() {
    if (this.isPausedFlag.isTrue) {
      this.setCurrentTime();
    }
  }

  private setCurrentTime() {
    runInAction(() => {
      this.state.currentTime = Number(
        (this.state.player?.currentTime ?? 0).toFixed(2),
      );
    });
  }

  private showControls() {
    if (this.shouldShowControlsFlag.isTrue) {
      return;
    }

    this.shouldShowControlsFlag.setTrue();
  }

  private startCurrentTimeTracking() {
    if (this.animationFrameId !== null) {
      return;
    }

    const updateCurrentTime = () => {
      if (!this.state.player || this.state.isPaused) {
        this.animationFrameId = null;

        return;
      }

      this.setCurrentTime();

      this.animationFrameId = requestAnimationFrame(updateCurrentTime);
    };

    this.animationFrameId = requestAnimationFrame(updateCurrentTime);
  }

  private stopCurrentTimeTracking() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
}
